import { useEffect } from 'react';

import {
  FALLBACK_PRIZE_LINES,
  FALLBACK_SEASON_TEXT,
  currentPrizeLines,
  currentSeasonText,
  prizePhraseOf,
  useDrawsStore,
} from '@/entities/draw';
import type { PrizeLine } from '@/entities/draw';
import { useLeaderboardStore } from '@/entities/leaderboard';
import type { PrizeTier } from '@/entities/leaderboard';
import { RADIUS, Skeleton, Squircle } from '@/shared/ui';

import styles from './DrawsPromo.module.scss';

/**
 * «Розыгрыши» (узел 438:51643).
 *
 * «Призы недели» приходят из `prizeTiers` недельного рейтинга — это те же
 * диапазоны мест, что и в турнирной сетке.
 *
 * «Призы месяца» и «Финальный приз» берутся из `/draws/current` — анонса,
 * который работает с первого дня цикла. Пока оператор не назначил призы
 * в админке, показывается текст макета: выдумывать приз нельзя.
 */
export function DrawsPromo() {
  const weekly = useLeaderboardStore((state) => state.weekly);
  const isLoading = useLeaderboardStore((state) => state.weeklyIsLoading);
  const error = useLeaderboardStore((state) => state.weeklyError);
  const load = useLeaderboardStore((state) => state.loadWeekly);

  // Именно анонс, а не результат: `/draws/monthly` отдаёт последний
  // ПРОВЕДЁННЫЙ розыгрыш, то есть весь следующий месяц показывал бы
  // призы предыдущего
  const current = useDrawsStore((state) => state.current);
  const loadCurrent = useDrawsStore((state) => state.loadCurrent);

  useEffect(() => {
    void load();
    void loadCurrent();
  }, [load, loadCurrent]);

  return (
    <div className={styles.page} data-testid="draws-promo">
      <WeekPrizes
        tiers={weekly?.prizeTiers ?? null}
        isLoading={isLoading}
        error={error}
      />
      <MonthPrizes lines={currentPrizeLines(current) ?? FALLBACK_PRIZE_LINES} />
      <FinalPrize text={currentSeasonText(current) ?? FALLBACK_SEASON_TEXT} />
    </div>
  );
}

function WeekPrizes({
  tiers,
  isLoading,
  error,
}: {
  tiers: PrizeTier[] | null;
  isLoading: boolean;
  error: string | null;
}) {
  return (
    <Squircle cornerRadius={RADIUS.xl} className={styles.week} data-testid="draws-week">
      <h2 className={styles.weekTitle}>Призы недели</h2>

      {isLoading && !tiers ? <Skeleton count={4} height={28} /> : null}

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}

      {tiers ? (
        <ul className={styles.tierList}>
          {tiers.map((tier) => (
            <li className={styles.tierRow} key={`${String(tier.rankFrom)}-${String(tier.rankTo)}`}>
              <span className={styles.tierLabel}>
                {tier.rankFrom === tier.rankTo
                  ? `Место ${String(tier.rankFrom)}`
                  : `Места ${String(tier.rankFrom)}-${String(tier.rankTo)}`}
              </span>
              <span className={styles.tierValues}>
                {tier.freebetReward > 0 ? (
                  <span className={styles.tierValue}>
                    <img
                      className={styles.freebetIcon}
                      src="/images/header/freebet-white.svg"
                      alt=""
                      aria-hidden
                    />
                    {tier.freebetReward}
                  </span>
                ) : null}
                <span className={styles.tierValue}>
                  <img className={styles.fireIcon} src="/images/header/fire-white.svg" alt="" aria-hidden />
                  {tier.pointsReward}
                </span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </Squircle>
  );
}

function MonthPrizes({ lines }: { lines: PrizeLine[] }) {
  return (
    <Squircle cornerRadius={RADIUS.xl} className={styles.month} data-testid="draws-month">
      <h2 className={styles.monthTitle}>Призы месяца</h2>
      <p className={styles.monthText}>
        {lines.map((line, index) => {
            const phrase = prizePhraseOf(line);
          return (
            <span key={line.poolType}>
              {index > 0 ? (
                <>
                  <br />
                  <br />
                </>
              ) : null}
              {phrase.before}
              <span className={styles.highlight}>{phrase.pool}</span>
              {phrase.between}
              <span className={styles.highlight}>{phrase.prize}</span>
            </span>
          );
        })}
      </p>
    </Squircle>
  );
}

function FinalPrize({ text }: { text: string }) {
  return (
    <Squircle cornerRadius={RADIUS.xl} className={styles.final} data-testid="draws-final">
      <h2 className={styles.finalTitle}>Финальный приз</h2>
      <p className={styles.finalText}>{text}</p>
    </Squircle>
  );
}
